// This file is auto-generated by @hey-api/openapi-ts

import { z } from 'zod';

/**
 * Analyst-assigned status for a carrier × coverage cell. When present
 * this value takes precedence over the status computed from the
 * underlying proposal data (`proposalStatus` + `coverageStatus`).
 *
 */
export const zResponsesTableManualResponseStatus = z.enum([
    'Incumbent',
    'Pending Proposal',
    'Not Requested',
    'Proposal Received',
    'DTQ',
    'Not Supported',
    'Not Applicable',
    'Not Quoted'
]);

/**
 * Lifecycle status of the carrier proposal that backs this cell.
 * Mirrors the values the proposals service writes to
 * `proposals.status`.
 *
 * - `pending` — proposal shell created at RFP submission; no
 * carrier response yet.
 * - `received` — carrier response was ingested. Inspect
 * `coverageStatus` to see whether the carrier quoted the
 * coverage or declined to quote it.
 * - `rejected` — proposal was rejected (reserved; not produced
 * by v1 of this view).
 * - `withdrawn` — proposal was withdrawn (reserved, as above).
 *
 */
export const zResponsesTableProposalStatus = z.enum([
    'pending',
    'received',
    'rejected',
    'withdrawn'
]);

/**
 * Per-coverage status reported by the carrier on a *received*
 * proposal. Mirrors the canonical v5
 * `ProposalCoverageStatusEnum`. Null when `proposalStatus` is
 * not `received`.
 *
 */
export const zResponsesTableCoverageStatus = z.enum(['quoted', 'declinedToQuote']);

export const zResponsesTableCarrierRef = z.object({
    id: z.string(),
    name: z.string(),
    sequence: z.number().int(),
    isIncumbent: z.boolean().optional()
});

export const zResponsesTableResponseCell = z.object({
    carrierId: z.string(),
    planGroupId: z.string(),
    proposalStatus: zResponsesTableProposalStatus,
    coverageStatus: zResponsesTableCoverageStatus.nullish(),
    proposalId: z.string().uuid().nullish(),
    receivedAt: z.string().datetime().nullish(),
    manualStatus: zResponsesTableManualResponseStatus.nullish()
});

export const zResponsesTableUpdateResponseCellRequest = z.object({
    carrierId: z.string(),
    planGroupId: z.string(),
    manualStatus: zResponsesTableManualResponseStatus
});

export const zResponsesVisibilityResponsesVisibilityResponse = z.object({
    rfpId: z.string(),
    hiddenCarrierIds: z.array(z.string()),
    hiddenPlanGroupIds: z.array(z.string())
});

/**
 * Partial update. Each field, when present, replaces the stored list;
 * omitted fields are left unchanged.
 *
 */
export const zResponsesVisibilityUpdateResponsesVisibilityRequest = z.object({
    hiddenCarrierIds: z.array(z.string()).optional(),
    hiddenPlanGroupIds: z.array(z.string()).optional()
});

/**
 * Lifecycle status of the carrier proposal that backs this
 * commission cell. Mirrors the values the proposals service
 * writes to `proposals.status`.
 *
 * - `pending` — proposal shell created at RFP submission;
 * no carrier response yet.
 * - `received` — carrier response was ingested. Inspect
 * `coverageStatus` to see whether the carrier quoted the
 * coverage or declined to quote it.
 * - `rejected` — proposal was rejected (reserved; not
 * produced by v1 of this view).
 * - `withdrawn` — proposal was withdrawn (reserved, as
 * above).
 *
 */
export const zCompsTableProposalStatus = z.enum([
    'pending',
    'received',
    'rejected',
    'withdrawn'
]);

/**
 * Per-coverage status reported by the carrier on a *received*
 * proposal. Mirrors the canonical v5
 * `ProposalCoverageStatusEnum`. Null when `proposalStatus`
 * is not `received`.
 *
 */
export const zCompsTableCoverageStatus = z.enum(['quoted', 'declinedToQuote']);

/**
 * Discriminator for non-coverage rows that appear beneath
 * the coverage rows on the Comps grid.
 *
 */
export const zCompsTableSubsidyRowKind = z.enum([
    'techCredit',
    'implementationCredit',
    'wellnessDollars'
]);

export const zCompsTableCarrierRef = z.object({
    id: z.string(),
    proposalId: z.string().uuid().nullish(),
    name: z.string(),
    sequence: z.number().int()
});

export const zCompsTableHeapedAmount = z.object({
    first: z.number().gte(0).lte(100),
    thereafter: z.number().gte(0).lte(100)
});

/**
 * A commission amount, modeled to support the variants used
 * by the v5 `CommissionTypeEnum`:
 *
 * - `percentage`, `graded` — `percent` is set; `flat` and
 * `heaped` are null.
 * - `pepm` — `flat` is set; `percent` and `heaped` are null.
 * - `heaped` — `heaped` is set; `percent` and `flat` are
 * null.
 * - `standard`, `net` — all three are null (no amount
 * carried).
 *
 * v1 models `graded` as a single percentage (the same
 * simplification used by the plan-designer commission
 * builder); a richer schema can be introduced later.
 *
 */
export const zCompsTableCommissionAmount = z.object({
    percent: z.number().gte(0).lte(100).nullish(),
    flat: z.number().gte(0).nullish(),
    heaped: zCompsTableHeapedAmount.nullish()
});

export const zCompsTableRequestedColumnRef = z.object({
    kind: z.enum(['requested'])
});

export const zCompsTableIncumbentColumnRef = z.object({
    kind: z.enum(['incumbent'])
});

export const zCompsTableCarrierColumnRef = z.object({
    kind: z.enum(['carrier']),
    carrierId: z.string()
});

/**
 * Discriminated reference to a column on the Comps grid.
 * Used by `NoteCell` and `SubsidyCell` to identify which
 * column a value belongs to without inlining the full
 * column descriptor on every cell.
 *
 * - `kind: requested` — the always-present *Requested
 * Commission* column (see `RequestedColumnRef`).
 * - `kind: incumbent` — the *Inforce Commission* column;
 * only valid when `incumbentColumn` is present (see
 * `IncumbentColumnRef`).
 * - `kind: carrier` — a carrier column; `carrierId` must
 * match an entry in `carriers[]` (see
 * `CarrierColumnRef`).
 *
 */
export const zCompsTableColumnRef = z.union([
    zCompsTableRequestedColumnRef,
    zCompsTableIncumbentColumnRef,
    zCompsTableCarrierColumnRef
]);

export const zCompsTableNoteCell = z.object({
    columnRef: zCompsTableColumnRef,
    text: z.string()
});

export const zCompsTableNotesRow = z.object({
    label: z.string(),
    cells: z.array(zCompsTableNoteCell)
});

export const zCompsTableSubsidyCell = z.object({
    columnRef: zCompsTableColumnRef,
    amount: z.number().nullish(),
    text: z.string().nullish()
});

export const zCompsTableSubsidyRow = z.object({
    kind: zCompsTableSubsidyRowKind,
    label: z.string(),
    sequence: z.number().int(),
    cells: z.array(zCompsTableSubsidyCell)
});

export const zCompsVisibilityCompsVisibilityResponse = z.object({
    rfpId: z.string(),
    configured: z.boolean(),
    hiddenRowGroupIds: z.array(z.string()),
    hiddenColumnIds: z.array(z.string())
});

/**
 * Partial update. Each field, when present, replaces the stored value;
 * omitted fields are left unchanged.
 *
 */
export const zCompsVisibilityUpdateCompsVisibilityRequest = z.object({
    configured: z.boolean().optional(),
    hiddenRowGroupIds: z.array(z.string()).optional(),
    hiddenColumnIds: z.array(z.string()).optional()
});

export const zCarriersCarrier = z.object({
    id: z.string(),
    name: z.string()
});

export const zCarriersCarrierListResponse = z.object({
    carriers: z.array(zCarriersCarrier)
});

export const zCarriersAppErrorResponse = z.object({
    appError: z.object({
        errorId: z.string().uuid(),
        statusCode: z.number().int(),
        message: z.string()
    })
});

export const zConfigurationsFinancialSettings = z.object({
    dollar_difference_from_current: z.boolean()
});

/**
 * Colors for the generated data-grid sheets (Financial Summary, Comp Subs Credits, Carrier Response).
 */
export const zConfigurationsGridStyle = z.object({
    banner_fill: z.string().optional(),
    header_fill: z.string().optional(),
    border_color: z.string().optional(),
    positive_fill: z.string().optional(),
    positive_text: z.string().optional(),
    negative_fill: z.string().optional(),
    negative_text: z.string().optional()
});

/**
 * Overrides for the workbook's Office theme color slots, recoloring the template-driven sheets.
 */
export const zConfigurationsThemeColors = z.object({
    light1: z.string().optional(),
    dark1: z.string().optional(),
    light2: z.string().optional(),
    dark2: z.string().optional(),
    accent1: z.string().optional(),
    accent2: z.string().optional(),
    accent3: z.string().optional(),
    accent4: z.string().optional(),
    accent5: z.string().optional(),
    accent6: z.string().optional(),
    hyperlink: z.string().optional(),
    followed_hyperlink: z.string().optional()
});

/**
 * Broker styling for the marketing-presentation export. All colors are
 * 6-digit hex strings (leading `#` optional); invalid values are dropped
 * and reported in `warnings`.
 *
 */
export const zConfigurationsPresentationStyle = z.object({
    grid: zConfigurationsGridStyle.optional(),
    theme: zConfigurationsThemeColors.optional()
});

export const zConfigurationsConfigurationData = z.object({
    quotes: z.record(z.record(z.boolean())),
    financial: zConfigurationsFinancialSettings,
    template: z.string().optional(),
    presentation_style: zConfigurationsPresentationStyle.optional()
});

export const zConfigurationsTemplateCatalogResponse = z.object({
    templates: z.array(z.object({
        name: z.string(),
        fileName: z.string()
    }))
});

export const zConfigurationsBrokerTemplatesResponse = z.object({
    templates: z.array(z.object({
        name: z.string(),
        configurationId: z.string().uuid(),
        configurationName: z.string()
    })),
    isDefaultConfiguration: z.boolean()
});

export const zConfigurationsSetRfpConfigurationRequest = z.object({
    configurationId: z.string().uuid()
});

export const zConfigurationsValidationIssue = z.object({
    field: z.string(),
    message: z.string()
});

export const zConfigurationsConfigurationResponse = z.object({
    id: z.string().uuid(),
    name: z.string(),
    status: z.enum([
        'draft',
        'published',
        'unpublished'
    ]),
    isDefault: z.boolean(),
    configuration: zConfigurationsConfigurationData,
    brokerCount: z.number().int(),
    brokerIds: z.array(z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' })).optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
});

export const zConfigurationsRfpConfigurationResponse = z.object({
    rfpId: z.string().uuid(),
    source: z.enum([
        'rfp',
        'default',
        'none'
    ]),
    configuration: zConfigurationsConfigurationResponse.nullable(),
    updatedBy: z.string().optional(),
    updatedAt: z.string().datetime().optional(),
    extendedFieldKeys: z.record(z.array(z.string())).optional()
});

export const zConfigurationsAssignmentResponse = z.object({
    id: z.string().uuid(),
    configurationId: z.string().uuid(),
    brokerId: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }),
    isSelected: z.boolean()
});

export const zConfigurationsConfigurationDetailResponse = zConfigurationsConfigurationResponse.and(z.object({
    assignments: z.array(zConfigurationsAssignmentResponse),
    warnings: z.array(zConfigurationsValidationIssue).optional()
}));

export const zConfigurationsListConfigurationsResponse = z.object({
    configurations: z.array(zConfigurationsConfigurationResponse)
});

export const zConfigurationsCreateConfigurationRequest = z.object({
    name: z.string().min(1),
    configuration: zConfigurationsConfigurationData.optional()
});

export const zConfigurationsUpdateConfigurationRequest = z.object({
    name: z.string().min(1).optional(),
    configuration: zConfigurationsConfigurationData.optional()
});

export const zConfigurationsSyncAssignmentsRequest = z.object({
    brokerIds: z.array(z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }))
});

/**
 * What the Run used, from its runner's report. Null while the Run is
 * open, and for an ended Run whose runner never reported.
 *
 */
export const zRumiNullableRunCost = z.object({
    model: z.string(),
    inputTokens: z.number().int().gte(0),
    outputTokens: z.number().int().gte(0),
    modelTurns: z.number().int().gte(0),
    toolCalls: z.number().int().gte(0)
}).nullable();

/**
 * What is written, and what Rumi read and cited to write it.
 */
export const zRumiCompleteCounts = z.object({
    coveragesWritten: z.number().int().gte(0).nullable(),
    coveragesRequested: z.number().int().gte(0).nullable(),
    plansWritten: z.number().int().gte(0).nullable(),
    plansRequested: z.number().int().gte(0).nullable(),
    documentsRead: z.number().int().gte(0).nullable(),
    evidenceCited: z.number().int().gte(0).nullable()
});

/**
 * The Run's follow-ups, by the reason Rumi gave for each. Follow-ups
 * have no resolved state, so these change when the next Run ends.
 *
 */
export const zRumiPartiallyExtractedCounts = z.object({
    sourceConflicts: z.number().int().gte(0),
    staleOrIncompleteSources: z.number().int().gte(0),
    toConfirm: z.number().int().gte(0)
});

/**
 * What an analyst must supply before the RFP can be submitted.
 */
export const zRumiAwaitingInputCounts = z.object({
    requiredFields: z.number().int().gte(0).nullable(),
    missingContributions: z.number().int().gte(0).nullable(),
    coveragesNotWritten: z.number().int().gte(0).nullable()
});

/**
 * The numbers behind the Launchpad's three summary tiles. Counts about
 * the RFP's data are read live, so they describe the RFP now; counts
 * about the Run come from what it recorded. A count is null when the
 * service it comes from did not answer, and the tile shows it as
 * unavailable.
 *
 * Requested coverages and plans are the broker's submission in
 * bp-api. Each requested coverage is matched to the btx coverage of
 * the same type, and to its rows of the product types the coverage's
 * plans name, or all of its rows when they name none. Each product is
 * counted in its own policy period: its `current` rows when it has
 * any, its `newBusiness` rows otherwise. A coverage
 * is written when one of its plans is; a plan is written when its
 * benefits hold a value or its rates hold an amount, and benefits a
 * benchmark seeded do not count until someone edits them.
 *
 */
export const zRumiRunCounts = z.object({
    complete: zRumiCompleteCounts,
    partiallyExtracted: zRumiPartiallyExtractedCounts,
    awaitingInput: zRumiAwaitingInputCounts
});

/**
 * One product of a coverage in one policy period, as a row of the Build
 * tab's coverage summary.
 *
 */
export const zRumiCoverageRow = z.object({
    name: z.string(),
    productType: z.string().nullable(),
    policyPeriod: z.enum(['current', 'newBusiness']),
    planCount: z.number().int().gte(0),
    status: z.enum([
        'complete',
        'incomplete',
        'awaiting-input'
    ])
});

/**
 * The document and page a statement came from, or null.
 */
export const zRumiNullableSource = z.object({
    documentId: z.string().uuid(),
    page: z.number().int().nullable()
}).nullable();

/**
 * One statement Rumi made, with its source when a document gave it.
 */
export const zRumiStatement = z.object({
    text: z.string(),
    source: zRumiNullableSource
});

/**
 * What Rumi recorded about one target: its summary, its notes, and
 * what it asks an analyst to do. Each is empty when Rumi recorded none.
 *
 */
export const zRumiFindings = z.object({
    summary: z.array(zRumiStatement),
    notes: z.array(zRumiStatement),
    followUps: z.array(zRumiStatement)
});

export const zRumiDocumentFindings = z.object({
    documentId: z.string().uuid()
}).and(zRumiFindings);

/**
 * Rumi's findings about the RFP's documents as a whole, and about
 * individual documents.
 *
 */
export const zRumiDocumentsFindings = zRumiFindings.and(z.object({
    byDocument: z.array(zRumiDocumentFindings)
}));

/**
 * One coverage and what Rumi found about it. Rumi records findings
 * against the coverage, never against one of its rows, so a coverage's
 * findings apply to all of its rows.
 *
 */
export const zRumiCoverageResult = z.object({
    coverageId: z.string().uuid(),
    coverageType: z.string(),
    name: z.string(),
    rows: z.array(zRumiCoverageRow)
}).and(zRumiFindings);

/**
 * What an ended Run found. Null while the Run is open.
 *
 */
export const zRumiNullableRunResults = z.object({
    rfp: zRumiFindings,
    clientDetails: zRumiFindings,
    rfpDetails: zRumiFindings,
    documents: zRumiDocumentsFindings,
    coverages: z.array(zRumiCoverageResult),
    counts: zRumiRunCounts
}).nullable();

/**
 * A Rumi Run, or null when the RFP has never had one.
 */
export const zRumiNullableRumiRun = z.object({
    seq: z.number().int().nullable(),
    startedAt: z.string().datetime(),
    open: z.boolean(),
    endedAt: z.string().datetime().nullable(),
    outcome: z.enum([
        'completed',
        'failed',
        'timed_out'
    ]).nullable(),
    errorSummary: z.string().nullable(),
    rumiStatus: z.enum(['done', 'stuck']).nullable(),
    runCost: zRumiNullableRunCost,
    results: zRumiNullableRunResults
}).nullable();

export const zRumiRumiRunResponse = z.object({
    run: zRumiNullableRumiRun
});

export const zRumiAppErrorResponse = z.object({
    appError: z.object({
        errorId: z.string().uuid(),
        statusCode: z.number().int(),
        message: z.string()
    })
});

export const zCorrespondenceThreadParticipant = z.object({
    id: z.string().uuid().optional(),
    address: z.string().optional(),
    displayName: z.string().nullish(),
    party: z.enum([
        'carrier',
        'broker',
        'internal',
        'unknown'
    ]).optional(),
    carrierId: z.string().nullish(),
    firstSeenAt: z.string().datetime().optional(),
    deliveryFailedAt: z.string().datetime().nullish(),
    deliveryFailedReason: z.string().nullish()
});

/**
 * One conversation. The thread's reply token is deliberately absent: it
 * is the secret in the Reply-To address, and no read path hands it out.
 *
 */
export const zCorrespondenceThread = z.object({
    id: z.string().uuid().optional(),
    rfpCanonicalIdentifier: z.string().uuid().nullish(),
    proposedRfpIdentifier: z.string().uuid().nullish(),
    subject: z.string().optional(),
    reference: z.string().optional(),
    audience: z.enum([
        'THREAD_AUDIENCE_CARRIER',
        'THREAD_AUDIENCE_BROKER',
        'THREAD_AUDIENCE_INTERNAL'
    ]).optional(),
    status: z.enum([
        'THREAD_STATUS_NEEDS_REPLY',
        'THREAD_STATUS_AWAITING_REPLY',
        'THREAD_STATUS_CLOSED'
    ]).optional(),
    triageOwnerEmail: z.string().nullish(),
    lastMessageAt: z.string().datetime().optional(),
    createdBy: z.string().optional(),
    createdAt: z.string().datetime().optional(),
    unreadCount: z.number().int().optional(),
    hasDraft: z.boolean().optional(),
    hasRumiDraft: z.boolean().optional(),
    participants: z.array(zCorrespondenceThreadParticipant).optional(),
    carrierName: z.string().optional(),
    tags: z.array(z.string()).optional()
});

export const zCorrespondenceMessageRecipient = z.object({
    role: z.enum(['RECIPIENT_ROLE_TO', 'RECIPIENT_ROLE_CC']).optional(),
    address: z.string().optional(),
    displayName: z.string().nullish()
});

export const zCorrespondenceAttachment = z.object({
    id: z.string().uuid().optional(),
    fileName: z.string().optional(),
    contentType: z.string().optional(),
    bytes: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }).optional(),
    isInline: z.boolean().optional(),
    scanStatus: z.enum([
        'pending',
        'clean',
        'infected',
        'failed'
    ]).optional(),
    downloadReady: z.boolean().optional()
});

export const zCorrespondenceDeliveryOutcome = z.object({
    status: z.string().optional(),
    failureReason: z.string().nullish(),
    occurredAt: z.string().datetime().nullish()
}).nullable();

export const zCorrespondenceMessage = z.object({
    id: z.string().uuid().optional(),
    threadId: z.string().uuid().optional(),
    direction: z.enum(['MESSAGE_DIRECTION_INBOUND', 'MESSAGE_DIRECTION_OUTBOUND']).optional(),
    status: z.enum([
        'MESSAGE_STATUS_DRAFT',
        'MESSAGE_STATUS_SENT',
        'MESSAGE_STATUS_DISCARDED',
        'MESSAGE_STATUS_QUARANTINED'
    ]).optional(),
    author: z.enum([
        'MESSAGE_AUTHOR_ANALYST',
        'MESSAGE_AUTHOR_RUMI',
        'MESSAGE_AUTHOR_EXTERNAL'
    ]).optional(),
    fromAddress: z.string().optional(),
    fromName: z.string().nullish(),
    bodyMarkdown: z.string().nullish(),
    bodyText: z.string().nullish(),
    bodyHtml: z.string().nullish(),
    answersMessageId: z.string().uuid().nullish(),
    authResults: z.record(z.unknown()).optional(),
    provenance: z.record(z.unknown()).optional(),
    sentAt: z.string().datetime().nullish(),
    receivedAt: z.string().datetime().nullish(),
    createdAt: z.string().datetime().optional(),
    recipients: z.array(zCorrespondenceMessageRecipient).optional(),
    attachments: z.array(zCorrespondenceAttachment).optional(),
    read: z.boolean().optional(),
    delivery: zCorrespondenceDeliveryOutcome.optional()
});

/**
 * Something that happened to the conversation that nobody wrote. Kept
 * apart from messages because an out-of-office stored as a reply reads as
 * the carrier answering.
 *
 */
export const zCorrespondenceThreadEvent = z.object({
    id: z.string().uuid().optional(),
    type: z.enum([
        'auto_reply',
        'delivery_failed',
        'quarantined',
        'attachment_infected',
        'reopened',
        'rfp_relinked'
    ]).optional(),
    messageId: z.string().uuid().nullish(),
    occurredAt: z.string().datetime().optional(),
    detail: z.record(z.unknown()).optional()
});

export const zCorrespondenceThreadReadResponse = z.object({
    thread: zCorrespondenceThread.optional(),
    messages: z.array(zCorrespondenceMessage).optional(),
    events: z.array(zCorrespondenceThreadEvent).optional(),
    writable: z.boolean().optional()
});

/**
 * One unsent reply, with the thread context a listing needs.
 */
export const zCorrespondenceDraft = z.object({
    messageId: z.string().uuid().optional(),
    threadId: z.string().uuid().optional(),
    subject: z.string().optional(),
    body: z.string().optional(),
    audience: z.enum([
        'THREAD_AUDIENCE_CARRIER',
        'THREAD_AUDIENCE_BROKER',
        'THREAD_AUDIENCE_INTERNAL'
    ]).optional(),
    author: z.enum(['MESSAGE_AUTHOR_ANALYST', 'MESSAGE_AUTHOR_RUMI']).optional(),
    recipients: z.array(zCorrespondenceMessageRecipient).optional(),
    draftedAt: z.string().datetime().optional()
});

export const zCorrespondenceMarkReadRequest = z.object({
    messageIds: z.array(z.string().uuid()).min(1).optional(),
    read: z.boolean(),
    wholeThread: z.boolean().optional()
});

export const zCorrespondenceUnreadCountResponse = z.object({
    unreadCount: z.number().int().optional()
});

export const zCorrespondenceDraftResponse = z.object({
    messageId: z.string().uuid().optional(),
    duplicate: z.boolean().optional()
});

/**
 * Type of commission paid to a Producer.
 */
export const zCommissionTypeEnum = z.enum([
    'heaped',
    'percentage',
    'standard',
    'graded',
    'net',
    'pepm'
]);

export const zCompsTableCommissionType = z.union([
    zCommissionTypeEnum,
    z.enum([
        'notApplicable',
        'notRequested',
        'notSupported',
        'notQuoted',
        'notDisclosed',
        'dtq'
    ])
]);

export const zCompsTableRequestedCommissionCell = z.object({
    planGroupId: z.string(),
    commissionType: zCompsTableCommissionType.nullish(),
    amount: zCompsTableCommissionAmount.nullish()
});

export const zCompsTableRequestedColumn = z.object({
    label: z.string(),
    cells: z.array(zCompsTableRequestedCommissionCell)
});

export const zCompsTableCommissionCell = z.object({
    carrierId: z.string(),
    planGroupId: z.string(),
    proposalId: z.string().uuid().nullish(),
    proposalStatus: zCompsTableProposalStatus,
    coverageStatus: zCompsTableCoverageStatus.nullish(),
    commissionType: zCompsTableCommissionType.nullish(),
    amount: zCompsTableCommissionAmount.nullish(),
    proposalPlanId: z.string().uuid().nullish(),
    receivedAt: z.string().datetime().nullish()
});

/**
 * Type of commission paid to a Producer.
 */
export const zCurrentCommissionTypeEnum = z.enum([
    'heaped',
    'percentage',
    'standard',
    'graded',
    'net',
    'pepm',
    'notApplicable'
]);

export const zCompsTableCurrentCommissionType = z.union([
    zCurrentCommissionTypeEnum,
    z.enum([
        'notRequested',
        'notSupported',
        'notQuoted',
        'notDisclosed',
        'dtq'
    ])
]);

export const zCompsTableIncumbentCommissionCell = z.object({
    planGroupId: z.string(),
    commissionType: zCompsTableCurrentCommissionType.nullish(),
    amount: zCompsTableCommissionAmount.nullish(),
    currentCarrierName: z.string().nullish()
});

export const zCompsTableIncumbentColumn = z.object({
    label: z.string(),
    cells: z.array(zCompsTableIncumbentCommissionCell)
});

/**
 * Types of coverage, aka line of coverage (LoC). We don't see standalone AD&D often. However, it is still a valid coverage type that is usually part of life plans.
 */
export const zCoverageTypeEnum = z.enum([
    'dental',
    'vision',
    'std',
    'ltd',
    'basicLifeAdnd',
    'voluntaryLifeAdnd',
    'accident',
    'criticalIllness',
    'hospitalIndemnity',
    'cancer',
    'fmla',
    'eap',
    'individualDisabilityInsurance',
    'wholeLife'
]);

export const zResponsesTableCoverageType = zCoverageTypeEnum;

export const zResponsesTableCoverageRef = z.object({
    planGroupId: z.string(),
    coverageType: zCoverageTypeEnum,
    label: z.string(),
    sequence: z.number().int()
});

export const zResponsesTableResponsesTableResponse = z.object({
    rfpId: z.string(),
    carriers: z.array(zResponsesTableCarrierRef),
    coverages: z.array(zResponsesTableCoverageRef),
    cells: z.array(zResponsesTableResponseCell)
});

export const zCompsTableCoverageType = zCoverageTypeEnum;

export const zCompsTableCoverageRef = z.object({
    planGroupId: z.string(),
    coverageType: zCoverageTypeEnum,
    label: z.string(),
    sequence: z.number().int(),
    rfpBenefitPlanGroupId: z.string().nullish(),
    planPosition: z.number().int().nullish()
});

export const zCompsTableCompsTableResponse = z.object({
    rfpId: z.string(),
    coverages: z.array(zCompsTableCoverageRef),
    carriers: z.array(zCompsTableCarrierRef),
    requestedColumn: zCompsTableRequestedColumn,
    incumbentColumn: zCompsTableIncumbentColumn.nullish(),
    cells: z.array(zCompsTableCommissionCell),
    notesRow: zCompsTableNotesRow,
    subsidyRows: z.array(zCompsTableSubsidyRow).optional()
});

/**
 * Override the RFP specification version for this request.
 * When omitted, the default version from the global feature
 * flag is used.
 *
 */
export const zResponsesTableRfpSpecVersion = z.string();

/**
 * Override the RFP specification version for this request.
 * When omitted, the default version from the global feature
 * flag is used.
 *
 */
export const zCompsTableRfpSpecVersion = z.string();

/**
 * Ecosystem RFP UUID
 */
export const zConfigurationsRfpId = z.string().uuid();

export const zConfigurationsConfigurationId = z.string().uuid();

/**
 * The numeric RFP id the portal routes on. The RFP UUID and the
 * ecosystem id are also accepted.
 *
 */
export const zRumiRfpId = z.string();

export const zCorrespondenceThreadId = z.string().uuid();

export const zResponsesTableGetResponsesTableHeaders = z.object({
    'X-RFP-Spec-Version': z.string().optional()
});

export const zResponsesTableGetResponsesTablePath = z.object({
    id: z.string()
});

/**
 * Responses table data for the RFP
 */
export const zResponsesTableGetResponsesTableResponse = zResponsesTableResponsesTableResponse;

export const zResponsesTableUpdateResponsesTableCellBody = zResponsesTableUpdateResponseCellRequest;

export const zResponsesTableUpdateResponsesTableCellPath = z.object({
    id: z.string()
});

/**
 * Cell status updated
 */
export const zResponsesTableUpdateResponsesTableCellResponse = zResponsesTableResponseCell;

export const zResponsesVisibilityGetResponsesVisibilityPath = z.object({
    id: z.string()
});

/**
 * Current hidden set for the RFP
 */
export const zResponsesVisibilityGetResponsesVisibilityResponse = zResponsesVisibilityResponsesVisibilityResponse;

export const zResponsesVisibilityUpdateResponsesVisibilityBody = zResponsesVisibilityUpdateResponsesVisibilityRequest;

export const zResponsesVisibilityUpdateResponsesVisibilityPath = z.object({
    id: z.string()
});

/**
 * Updated hidden set for the RFP
 */
export const zResponsesVisibilityUpdateResponsesVisibilityResponse = zResponsesVisibilityResponsesVisibilityResponse;

export const zCompsTableGetCompsTableHeaders = z.object({
    'X-RFP-Spec-Version': z.string().optional()
});

export const zCompsTableGetCompsTablePath = z.object({
    id: z.string()
});

/**
 * Comps table data for the RFP
 */
export const zCompsTableGetCompsTableResponse = zCompsTableCompsTableResponse;

export const zCompsVisibilityGetCompsVisibilityPath = z.object({
    id: z.string()
});

/**
 * Current hidden set for the RFP
 */
export const zCompsVisibilityGetCompsVisibilityResponse = zCompsVisibilityCompsVisibilityResponse;

export const zCompsVisibilityUpdateCompsVisibilityBody = zCompsVisibilityUpdateCompsVisibilityRequest;

export const zCompsVisibilityUpdateCompsVisibilityPath = z.object({
    id: z.string()
});

/**
 * Updated hidden set for the RFP
 */
export const zCompsVisibilityUpdateCompsVisibilityResponse = zCompsVisibilityCompsVisibilityResponse;

/**
 * Carrier list
 */
export const zCarriersGetCarriersResponse = zCarriersCarrierListResponse;

/**
 * List of configurations
 */
export const zConfigurationsListConfigurationsResponse2 = zConfigurationsListConfigurationsResponse;

export const zConfigurationsCreateConfigurationBody = zConfigurationsCreateConfigurationRequest;

/**
 * Created configuration
 */
export const zConfigurationsCreateConfigurationResponse = zConfigurationsConfigurationDetailResponse;

export const zConfigurationsDeleteConfigurationPath = z.object({
    id: z.string().uuid()
});

/**
 * Deleted
 */
export const zConfigurationsDeleteConfigurationResponse = z.void();

export const zConfigurationsGetConfigurationPath = z.object({
    id: z.string().uuid()
});

/**
 * Configuration detail
 */
export const zConfigurationsGetConfigurationResponse = zConfigurationsConfigurationDetailResponse;

export const zConfigurationsUpdateConfigurationBody = zConfigurationsUpdateConfigurationRequest;

export const zConfigurationsUpdateConfigurationPath = z.object({
    id: z.string().uuid()
});

/**
 * Updated configuration detail
 */
export const zConfigurationsUpdateConfigurationResponse = zConfigurationsConfigurationDetailResponse;

export const zConfigurationsPublishConfigurationPath = z.object({
    id: z.string().uuid()
});

/**
 * Published configuration
 */
export const zConfigurationsPublishConfigurationResponse = zConfigurationsConfigurationResponse;

export const zConfigurationsUnpublishConfigurationPath = z.object({
    id: z.string().uuid()
});

/**
 * Unpublished configuration
 */
export const zConfigurationsUnpublishConfigurationResponse = zConfigurationsConfigurationResponse;

/**
 * Template catalog
 */
export const zConfigurationsListMarketingPresentationTemplateCatalogResponse = zConfigurationsTemplateCatalogResponse;

export const zConfigurationsGetRfpConfigurationPath = z.object({
    rfpId: z.string().uuid()
});

/**
 * The resolved configuration
 */
export const zConfigurationsGetRfpConfigurationResponse = zConfigurationsRfpConfigurationResponse;

export const zConfigurationsSetRfpConfigurationBody = zConfigurationsSetRfpConfigurationRequest;

export const zConfigurationsSetRfpConfigurationPath = z.object({
    rfpId: z.string().uuid()
});

/**
 * The stored selection
 */
export const zConfigurationsSetRfpConfigurationResponse = zConfigurationsRfpConfigurationResponse;

export const zConfigurationsListBrokerConfigurationsPath = z.object({
    brokerId: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' })
});

/**
 * The broker's selectable configurations
 */
export const zConfigurationsListBrokerConfigurationsResponse = zConfigurationsListConfigurationsResponse;

export const zConfigurationsListBrokerMarketingPresentationTemplatesPath = z.object({
    brokerId: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' })
});

/**
 * The broker's selectable templates
 */
export const zConfigurationsListBrokerMarketingPresentationTemplatesResponse = zConfigurationsBrokerTemplatesResponse;

export const zConfigurationsSyncBrokerAssignmentsBody = zConfigurationsSyncAssignmentsRequest;

export const zConfigurationsSyncBrokerAssignmentsPath = z.object({
    id: z.string().uuid()
});

/**
 * Assignments synced
 */
export const zConfigurationsSyncBrokerAssignmentsResponse = z.void();

export const zRumiGetLatestRumiRunPath = z.object({
    id: z.string()
});

/**
 * The latest Run, or none
 */
export const zRumiGetLatestRumiRunResponse = zRumiRumiRunResponse;

export const zRumiStartRumiRunPath = z.object({
    id: z.string()
});

/**
 * The Run started
 */
export const zRumiStartRumiRunResponse = zRumiRumiRunResponse;

export const zCorrespondenceListCorrespondenceThreadsQuery = z.object({
    rfpId: z.string().uuid().optional(),
    queue: z.boolean().optional(),
    status: z.enum([
        'needs_reply',
        'awaiting_reply',
        'closed'
    ]).optional(),
    mine: z.boolean().optional(),
    limit: z.number().int().optional(),
    cursor: z.string().optional()
});

/**
 * Matching threads, most recently active first
 */
export const zCorrespondenceListCorrespondenceThreadsResponse = z.object({
    threads: z.array(zCorrespondenceThread).optional(),
    nextCursor: z.string().optional()
});

export const zCorrespondenceListCorrespondenceDraftsQuery = z.object({
    rfpId: z.string(),
    limit: z.number().int().optional(),
    cursor: z.string().optional()
});

/**
 * The unsent replies on that RFP
 */
export const zCorrespondenceListCorrespondenceDraftsResponse = z.object({
    drafts: z.array(zCorrespondenceDraft).optional(),
    nextCursor: z.string().optional()
});

export const zCorrespondenceReadCorrespondenceThreadPath = z.object({
    threadId: z.string().uuid()
});

/**
 * The thread, its messages, and its timeline
 */
export const zCorrespondenceReadCorrespondenceThreadResponse = zCorrespondenceThreadReadResponse;

export const zCorrespondenceMarkCorrespondenceReadBody = zCorrespondenceMarkReadRequest;

export const zCorrespondenceMarkCorrespondenceReadPath = z.object({
    threadId: z.string().uuid()
});

/**
 * The thread's unread count after the write
 */
export const zCorrespondenceMarkCorrespondenceReadResponse = zCorrespondenceUnreadCountResponse;

export const zCorrespondenceDiscardCorrespondenceDraftPath = z.object({
    threadId: z.string().uuid(),
    messageId: z.string().uuid()
});

/**
 * The draft was discarded, or already was
 */
export const zCorrespondenceDiscardCorrespondenceDraftResponse = z.object({
    discarded: z.boolean().optional()
});

export const zCorrespondenceClaimCorrespondenceThreadPath = z.object({
    threadId: z.string().uuid()
});

/**
 * The thread, now owned by the caller
 */
export const zCorrespondenceClaimCorrespondenceThreadResponse = z.object({
    thread: zCorrespondenceThread.optional()
});

export const zCorrespondenceLinkCorrespondenceThreadBody = z.object({
    rfpCanonicalIdentifier: z.string().uuid()
});

export const zCorrespondenceLinkCorrespondenceThreadPath = z.object({
    threadId: z.string().uuid()
});

/**
 * The thread, now linked
 */
export const zCorrespondenceLinkCorrespondenceThreadResponse = z.object({
    thread: zCorrespondenceThread.optional()
});

export const zCorrespondenceDraftCorrespondenceReplyBody = z.object({
    answersMessageId: z.string().uuid(),
    bodyMarkdown: z.string().min(1)
});

export const zCorrespondenceDraftCorrespondenceReplyPath = z.object({
    threadId: z.string().uuid()
});

/**
 * The draft already existed and was returned unchanged
 */
export const zCorrespondenceDraftCorrespondenceReplyResponse = zCorrespondenceDraftResponse;

export const zCorrespondenceSendCorrespondenceReplyBody = z.object({
    messageId: z.string().uuid()
});

export const zCorrespondenceSendCorrespondenceReplyPath = z.object({
    threadId: z.string().uuid()
});

/**
 * Sent, and the delivery recorded (not reachable yet)
 */
export const zCorrespondenceSendCorrespondenceReplyResponse = z.object({
    messageId: z.string().uuid().optional(),
    deliveryId: z.string().uuid().optional()
});
